/**
 * EditableImageNode - 可编辑图片节点（Inline 编辑）
 *
 * 特性：
 * - DecoratorNode：渲染为 inline 编辑面板
 * - 双击图片时替换为此节点
 * - 按钮保存/取消
 * - 保持在文本流中
 */

import {
  $applyNodeReplacement,
  $getSelection,
  $isRangeSelection,
  DecoratorNode,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  Spread,
} from "lexical";
import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useLayoutEffect, useRef, useState } from "react";
import { $createImageNode } from "./ImageNode";

declare global {
  interface Window {
    editor?: LexicalEditor;
  }
}

export type SerializedEditableImageNode = Spread<
  {
    type: "editableImage";
    version: 1;
    src: string;
    altText?: string;
    width?: number;
    height?: number;
  },
  SerializedLexicalNode
>;

export class EditableImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string | undefined;
  __width: number | undefined;
  __height: number | undefined;

  static getType(): string {
    return "editableImage";
  }

  static clone(node: EditableImageNode): EditableImageNode {
    return new EditableImageNode(node.__src, node.__altText, node.__width, node.__height, node.__key);
  }

  constructor(src: string, altText?: string, width?: number, height?: number, key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width;
    this.__height = height;
  }

  createDOM(): HTMLElement {
    const div = document.createElement("div");
    div.style.display = "inline-block";
    div.style.verticalAlign = "top";
    return div;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return (
      <EditableImageEditor src={this.__src} altText={this.__altText} width={this.__width} height={this.__height} nodeKey={this.__key} />
    );
  }

  static importJSON(serializedNode: SerializedEditableImageNode): EditableImageNode {
    return new EditableImageNode(serializedNode.src, serializedNode.altText, serializedNode.width, serializedNode.height);
  }

  exportJSON(): SerializedEditableImageNode {
    return {
      ...super.exportJSON(),
      type: "editableImage",
      version: 1,
      src: this.__src,
      altText: this.__altText,
      width: this.__width,
      height: this.__height,
    };
  }

  getTextContent(): string {
    return `[图片: ${this.__altText || this.__src}]`;
  }

  isEqualTo(other: LexicalNode): boolean {
    return !(other instanceof EditableImageNode) || this.__src !== other.__src;
  }
}

export function $createEditableImageNode(src: string, altText?: string, width?: number, height?: number): EditableImageNode {
  const node = new EditableImageNode(src, altText, width, height);
  return $applyNodeReplacement(node);
}

function EditableImageEditor({
  src,
  altText,
  width,
  height,
  nodeKey,
}: {
  src: string;
  altText?: string;
  width?: number;
  height?: number;
  nodeKey: string;
}) {
  const [imageSrc, setImageSrc] = useState(src);
  const [imageAlt, setImageAlt] = useState(altText || "");
  const [imageWidth, setImageWidth] = useState(width?.toString() || "");
  const [imageHeight, setImageHeight] = useState(height?.toString() || "");
  const [error, setError] = useState<string>("");
  const inputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement)[]>([]);

  // Validate image URL
  const validateImageUrl = (url: string): string => {
    if (!url || !url.trim()) {
      return "图片 URL 不能为空";
    }

    const lowerUrl = url.toLowerCase().trim();

    // Allow data URLs
    if (lowerUrl.startsWith("data:image/")) {
      return "";
    }

    // Check if URL has a valid image extension
    // Extract the path before query string or hash
    const urlPath = url.split("?")[0].split("#")[0].toLowerCase();
    const validExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp", ".ico", ".avif"];

    // Check for valid image extension in path
    const hasValidExtension = validExtensions.some((ext) => urlPath.endsWith(ext));
    if (hasValidExtension) {
      return "";
    }

    // Check for format parameter in query string (e.g., ?format=jpg)
    const urlObj = new URL(url);
    const formatParam = urlObj.searchParams.get("format");
    if (formatParam && validExtensions.some((ext) => `.${formatParam}` === ext)) {
      return "";
    }

    return `请输入有效的图片 URL (支持: ${validExtensions.join(", ")})`;
  };

  // 定义保存和取消函数（需要在 useEffect 之前）
  const handleSave = useCallback(() => {
    // Trim and validate URL
    const trimmedSrc = imageSrc.trim();
    const validationError = validateImageUrl(trimmedSrc);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Convert to regular ImageNode
    const editor = (window as any).editor;
    if (!editor) {
      return;
    }

    editor.update(() => {
      const node = editor.getEditorState()._nodeMap.get(nodeKey);

      if (node instanceof EditableImageNode) {
        const imageNode = $createImageNode(
          trimmedSrc,
          imageAlt || undefined,
          imageWidth ? parseInt(imageWidth, 10) : undefined,
          imageHeight ? parseInt(imageHeight, 10) : undefined,
        );
        node.replace(imageNode);

        // Move selection after the image
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          imageNode.selectEnd();
        }
      }
    });
  }, [imageSrc, imageAlt, imageWidth, imageHeight, nodeKey, src, altText, width, height]);

  const handleCancel = useCallback(() => {
    // Revert to original image
    const editor = (window as any).editor;
    if (!editor) {
      return;
    }

    editor.update(() => {
      const node = editor.getEditorState()._nodeMap.get(nodeKey);

      if (node instanceof EditableImageNode) {
        const imageNode = $createImageNode(src, altText, width, height);
        node.replace(imageNode);
      }
    });
  }, [src, altText, width, height, nodeKey]);

  // Auto-focus first input when mounted
  useLayoutEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
      inputRefs.current[0].select();
    }
  }, []);

  const handleEditorKeyDownCapture = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      const key = event.key;
      const isCmdOrCtrl = event.metaKey || event.ctrlKey;
      const nativeEvent = event.nativeEvent as KeyboardEvent;

      if (key === "Escape" || key === "Esc") {
        event.preventDefault();
        event.stopPropagation();
        nativeEvent.stopImmediatePropagation?.();
        handleCancel();
        return;
      }

      if (key === "Enter" && event.target === inputRefs.current[0]) {
        event.preventDefault();
        event.stopPropagation();
        nativeEvent.stopImmediatePropagation?.();
        handleSave();
        return;
      }

      if (isCmdOrCtrl) {
        event.stopPropagation();
        nativeEvent.stopImmediatePropagation?.();
      }
    },
    [handleCancel, handleSave],
  );

  const setInputRef = (index: number) => (el: HTMLInputElement | HTMLTextAreaElement | null) => {
    if (el !== null) {
      inputRefs.current[index] = el;
    }
  };

  return (
    <div className="inline-block bg-background border rounded-lg shadow-lg p-4 m-2 w-80" onKeyDownCapture={handleEditorKeyDownCapture}>
      <div className="mb-3">
        <h3 className="text-sm font-semibold">编辑图片</h3>
        <p className="text-xs text-muted-foreground">输入图片 URL 和属性</p>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium">图片 URL *</label>
          <input
            ref={setInputRef(0)}
            type="url"
            value={imageSrc}
            onChange={(e) => {
              setImageSrc(e.target.value);
              setError(""); // Clear error on input change
            }}
            className={error ? "h-8 text-sm border-2 border-destructive rounded px-2" : "h-8 text-sm border rounded px-2"}
            placeholder="https://example.com/image.jpg"
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium">描述（Alt Text）</label>
          <textarea
            ref={setInputRef(1)}
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
            rows={2}
            className="text-sm resize-none border rounded p-2"
            placeholder="图片描述..."
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium">宽度</label>
            <input
              ref={setInputRef(2)}
              type="number"
              value={imageWidth}
              onChange={(e) => setImageWidth(e.target.value)}
              className="h-8 text-sm border rounded px-2"
              placeholder="自动"
              min="1"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium">高度</label>
            <input
              ref={setInputRef(3)}
              type="number"
              value={imageHeight}
              onChange={(e) => setImageHeight(e.target.value)}
              className="h-8 text-sm border rounded px-2"
              placeholder="自动"
              min="1"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-2 mt-4 pt-3 border-t">
        <button
          type="button"
          onClick={handleSave}
          disabled={!imageSrc.trim()}
          className="flex-1 h-8 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          保存
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="flex-1 h-8 text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded"
        >
          取消
        </button>
      </div>
    </div>
  );
}
